import type {
  FC,
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react'
import { useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  type AnimationPlaybackControls,
} from 'motion/react'
import Box from '@mui/material/Box'
import Button, { type ButtonProps } from '@mui/material/Button'
import { alpha } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'
import { DrawnCheck } from '@/components/DrawnCheck'
import useTranslate from '@/hooks/use-translate'

/** Durée du maintien. */
export const HOLD_TO_CONFIRM_MS = 1200
/** La coche reste un instant avant l'action : un dialogue qui se ferme aussitôt ne la montrerait pas. */
export const HOLD_CONFIRMED_DELAY_MS = 350
/** Au-delà de cette distance du bouton, le pointeur annule le maintien. */
const CANCEL_DISTANCE_PX = 24
/** Un appui plus court est un clic : le bouton dit alors qu'il faut maintenir. */
const NUDGE_BELOW_MS = 250
const NUDGE_MS = 1600

const RELEASE_SPRING = { type: 'spring', stiffness: 260, damping: 30 } as const

export type HoldToConfirmButtonProps = Omit<
  ButtonProps,
  'children' | 'onClick' | 'variant'
> & {
  label: string
  onConfirm: () => void
  /** Durée du maintien, en ms. */
  duration?: number
}

/**
 * Bouton « maintenir pour confirmer » (TEC-1956), pour les suppressions : le bouton se remplit pendant l'appui,
 * revient en ressort si on relâche trop tôt (un nouvel appui reprend où il en était), puis se coche. Au clavier,
 * maintenir Espace ou Entrée. S'éloigner de plus de 24 px ou quitter le focus annule.
 */
export const HoldToConfirmButton: FC<HoldToConfirmButtonProps> = ({
  label,
  onConfirm,
  duration = HOLD_TO_CONFIRM_MS,
  color = 'error',
  disabled,
  sx,
  ...rest
}) => {
  const { t } = useTranslate('common')
  const reduceMotion = useReducedMotion() ?? false
  const hintId = useId()
  const progress = useMotionValue(0)
  const controls = useRef<AnimationPlaybackControls | null>(null)
  const completeTimer = useRef<number | undefined>(undefined)
  const holdStartedAt = useRef(0)
  // Ref et état : l'appui et le relâchement peuvent tomber avant le rendu qui suit l'appui.
  const holdingRef = useRef(false)
  const [holding, setHolding] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const [nudge, setNudge] = useState(false)
  const onConfirmRef = useRef(onConfirm)
  onConfirmRef.current = onConfirm

  useEffect(
    () => () => {
      controls.current?.stop()
      window.clearTimeout(completeTimer.current)
    },
    []
  )

  useEffect(() => {
    if (!nudge) return
    const id = window.setTimeout(() => setNudge(false), NUDGE_MS)
    return () => window.clearTimeout(id)
  }, [nudge])

  useEffect(() => {
    if (!confirmed) return
    const id = window.setTimeout(
      () => onConfirmRef.current(),
      HOLD_CONFIRMED_DELAY_MS
    )
    return () => window.clearTimeout(id)
  }, [confirmed])

  const start = useCallback(() => {
    if (disabled || confirmed || holdingRef.current) return
    holdingRef.current = true
    setHolding(true)
    setNudge(false)
    holdStartedAt.current = Date.now()
    // Le reste du maintien : un appui qui reprend repart d'où le remplissage était revenu.
    const remainingMs = duration * (1 - progress.get())
    controls.current?.stop()
    controls.current = animate(progress, 1, {
      duration: remainingMs / 1000,
      ease: 'linear',
    })
    // La confirmation suit l'horloge, pas l'animation : elle ne dépend pas des images affichées.
    completeTimer.current = window.setTimeout(() => {
      holdingRef.current = false
      setHolding(false)
      setConfirmed(true)
      progress.set(1)
      navigator.vibrate?.(20)
    }, remainingMs)
  }, [confirmed, disabled, duration, progress])

  const cancel = useCallback(() => {
    if (!holdingRef.current) return
    holdingRef.current = false
    setHolding(false)
    window.clearTimeout(completeTimer.current)
    if (Date.now() - holdStartedAt.current < NUDGE_BELOW_MS) setNudge(true)
    controls.current?.stop()
    controls.current = animate(
      progress,
      0,
      reduceMotion ? { duration: 0.15 } : RELEASE_SPRING
    )
  }, [progress, reduceMotion])

  const onPointerMove = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!holdingRef.current) return
    const rect = event.currentTarget.getBoundingClientRect()
    const dx = Math.max(
      rect.left - event.clientX,
      0,
      event.clientX - rect.right
    )
    const dy = Math.max(
      rect.top - event.clientY,
      0,
      event.clientY - rect.bottom
    )
    if (Math.hypot(dx, dy) > CANCEL_DISTANCE_PX) cancel()
  }

  const isHoldKey = (event: KeyboardEvent) =>
    event.key === ' ' || event.key === 'Enter'

  const text = nudge ? t('holdToConfirmNudge') : label

  return (
    <>
      <Button
        {...rest}
        variant="contained"
        color={color}
        disabled={disabled}
        aria-label={label}
        aria-describedby={hintId}
        aria-disabled={confirmed || undefined}
        data-holding={holding || undefined}
        onPointerDown={(event) => {
          // Clic droit ou molette : pas un maintien.
          if (event.button > 0) return
          event.currentTarget.setPointerCapture?.(event.pointerId)
          start()
        }}
        onPointerUp={cancel}
        onPointerCancel={cancel}
        onPointerMove={onPointerMove}
        onBlur={cancel}
        onKeyDown={(event) => {
          if (!isHoldKey(event)) return
          // Sans ça, Entrée ou Espace « cliquerait » : seul le maintien confirme.
          event.preventDefault()
          if (!event.repeat) start()
        }}
        onKeyUp={(event) => {
          if (isHoldKey(event)) cancel()
        }}
        onClick={(event) => event.preventDefault()}
        sx={[
          {
            overflow: 'hidden',
            position: 'relative',
            touchAction: 'none',
            transition: (theme) =>
              `${theme.transitions.create([
                'background-color',
                'box-shadow',
              ])}, transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1)`,
            userSelect: 'none',
            '&[data-holding]': reduceMotion ? {} : { transform: 'scale(0.97)' },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Box
          component={motion.span}
          aria-hidden
          data-testid="hold-to-confirm-fill"
          style={{ scaleX: progress }}
          sx={{
            backgroundColor: (theme) => alpha(theme.palette.common.black, 0.22),
            inset: 0,
            position: 'absolute',
            transformOrigin: 'left center',
          }}
        />
        <Box
          component="span"
          sx={{
            alignItems: 'center',
            display: 'inline-flex',
            gap: 1,
            position: 'relative',
          }}
        >
          {confirmed ? (
            <Box component="span" sx={{ display: 'inline-flex', fontSize: 18 }}>
              <DrawnCheck animate={!reduceMotion} />
            </Box>
          ) : null}
          {text}
        </Box>
      </Button>
      <Box component="span" id={hintId} sx={visuallyHidden}>
        {t('holdToConfirmHint')}
      </Box>
      <Box component="span" role="status" sx={visuallyHidden}>
        {confirmed ? `${label} : ${t('confirmed')}` : ''}
      </Box>
    </>
  )
}

export default HoldToConfirmButton
